import React, { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Stack,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  CircularProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/Delete"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import InventoryIcon from "@mui/icons-material/Inventory"
import ReceiptIcon from "@mui/icons-material/Receipt"
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing"
import PendingIcon from "@mui/icons-material/Pending"
import SaveIcon from "@mui/icons-material/Save"
import TaskAltIcon from "@mui/icons-material/TaskAlt"
import FactCheckIcon from "@mui/icons-material/FactCheck"

import { fetchWorkflowConfig, saveWorkflowConfig } from "../../store/workflow/actions"

// Predefined statuses with their actions (user can only select from these)
const AVAILABLE_STATUSES = [
  { code: "CONFIRMED", name: "Confirmed", color: "#3b82f6", icon: CheckCircleIcon, description: "Order confirmed by staff" },
  { code: "STOCK_CHECK", name: "Stock Check", color: "#8b5cf6", icon: InventoryIcon, description: "Check material availability" },
  { code: "PRODUCTION", name: "In Production", color: "#f97316", icon: PrecisionManufacturingIcon, description: "Manufacturing in progress" },
  { code: "READY", name: "Ready", color: "#10b981", icon: TaskAltIcon, description: "Ready for invoicing" },
  { code: "INVOICED", name: "Invoiced", color: "#06b6d4", icon: ReceiptIcon, description: "Invoice created" },
  { code: "QUALITY_CHECK", name: "Quality Check", color: "#ec4899", icon: FactCheckIcon, description: "Quality inspection" },
]

const WorkflowEditorTab = ({ onSnackbar }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { config, configLoading, configSaving } = useSelector((state) => state.workflow)

  // Selected statuses in order
  const [selectedStatuses, setSelectedStatuses] = useState([])
  const [dialogOpen, setDialogOpen] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  // Load config on mount
  useEffect(() => {
    dispatch(fetchWorkflowConfig())
  }, [dispatch])

  // Parse config into selected statuses
  useEffect(() => {
    if (config?.statuses) {
      const middleCodes = config.statuses
        .filter((s) => s.code !== "PENDING" && s.code !== "SHIPPED" && s.code !== "CANCELLED")
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        .map((s) => s.code)
      setSelectedStatuses(middleCodes)
      setHasChanges(false)
    }
  }, [config])

  // Get available statuses not yet selected
  const availableToAdd = AVAILABLE_STATUSES.filter((s) => !selectedStatuses.includes(s.code))

  const handleAddStatus = (statusCode) => {
    setSelectedStatuses((prev) => [...prev, statusCode])
    setDialogOpen(false)
    setHasChanges(true)
  }

  const handleRemoveStatus = (index) => {
    setSelectedStatuses((prev) => prev.filter((_, i) => i !== index))
    setHasChanges(true)
  }

  const handleSave = () => {
    // Build full status list
    const allStatuses = [
      { code: "PENDING", name: "Pending", color: "#f59e0b", sort_order: 0, is_initial: true },
      ...selectedStatuses.map((code, i) => {
        const status = AVAILABLE_STATUSES.find((s) => s.code === code)
        return { ...status, sort_order: i + 1 }
      }),
      { code: "SHIPPED", name: "Shipped", color: "#16a34a", sort_order: selectedStatuses.length + 1, is_terminal: true },
    ]

    // Build transitions (linear flow)
    const transitions = []
    for (let i = 0; i < allStatuses.length - 1; i++) {
      transitions.push({
        from: allStatuses[i].code,
        to: allStatuses[i + 1].code,
        name: `${allStatuses[i].code}_to_${allStatuses[i + 1].code}`,
        label: allStatuses[i + 1].name,
      })
    }

    const templateId = config?.id || 1
    dispatch(saveWorkflowConfig(templateId, { statuses: allStatuses, transitions }))
    onSnackbar?.({ open: true, message: t("Workflow saved"), severity: "success" })
    setHasChanges(false)
  }

  const getStatusInfo = (code) => AVAILABLE_STATUSES.find((s) => s.code === code)

  if (configLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {t("Order Workflow")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("Define the status flow for orders")}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={!hasChanges || configSaving}
          sx={{ bgcolor: "#3b82f6" }}
        >
          {configSaving ? t("Saving...") : t("Save")}
        </Button>
      </Stack>

      {/* Workflow Line */}
      <Paper elevation={0} sx={{ p: 3, border: "1px solid #e2e8f0", borderRadius: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ overflowX: "auto", pb: 1 }}
        >
          {/* Start Status - Fixed */}
          <Tooltip title={t("Start status (fixed)")}>
            <Box sx={{ textAlign: "center", minWidth: 80 }}>
              <Avatar sx={{ bgcolor: "#f59e0b", mx: "auto", mb: 0.5 }}>
                <PendingIcon />
              </Avatar>
              <Typography variant="caption" fontWeight={500}>
                {t("Pending")}
              </Typography>
            </Box>
          </Tooltip>

          <ArrowForwardIcon sx={{ color: "#9ca3af" }} />

          {/* Selected Statuses */}
          {selectedStatuses.map((code, index) => {
            const status = getStatusInfo(code)
            if (!status) return null
            const IconComp = status.icon
            return (
              <React.Fragment key={code}>
                <Tooltip title={status.description}>
                  <Box sx={{ textAlign: "center", minWidth: 80, position: "relative" }}>
                    <Avatar sx={{ bgcolor: status.color, mx: "auto", mb: 0.5 }}>
                      <IconComp />
                    </Avatar>
                    <Typography variant="caption" fontWeight={500} display="block">
                      {t(status.name)}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveStatus(index)}
                      sx={{ color: "#ef4444", mt: 0.5 }}
                    >
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                </Tooltip>
                <ArrowForwardIcon sx={{ color: "#9ca3af" }} />
              </React.Fragment>
            )
          })}

          {/* Add Status Button */}
          {availableToAdd.length > 0 && (
            <>
              <Tooltip title={t("Add status")}>
                <IconButton
                  onClick={() => setDialogOpen(true)}
                  sx={{
                    border: "2px dashed #cbd5e1",
                    borderRadius: 2,
                    width: 48,
                    height: 48,
                    "&:hover": { borderColor: "#3b82f6", bgcolor: "#eff6ff" },
                  }}
                >
                  <AddIcon sx={{ color: "#64748b" }} />
                </IconButton>
              </Tooltip>
              <ArrowForwardIcon sx={{ color: "#9ca3af" }} />
            </>
          )}

          {/* End Status - Fixed */}
          <Tooltip title={t("End status (fixed)")}>
            <Box sx={{ textAlign: "center", minWidth: 80 }}>
              <Avatar sx={{ bgcolor: "#16a34a", mx: "auto", mb: 0.5 }}>
                <LocalShippingIcon />
              </Avatar>
              <Typography variant="caption" fontWeight={500}>
                {t("Shipped")}
              </Typography>
            </Box>
          </Tooltip>
        </Stack>
      </Paper>

      {/* Add Status Dialog - Select from available */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("Add Status")}</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {availableToAdd.length === 0 ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography color="text.secondary">{t("All statuses added")}</Typography>
            </Box>
          ) : (
            <List>
              {availableToAdd.map((status) => {
                const IconComp = status.icon
                return (
                  <ListItemButton
                    key={status.code}
                    onClick={() => handleAddStatus(status.code)}
                    sx={{ "&:hover": { bgcolor: "#f8fafc" } }}
                  >
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: status.color, width: 36, height: 36 }}>
                        <IconComp sx={{ fontSize: 20 }} />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={t(status.name)}
                      secondary={t(status.description)}
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                  </ListItemButton>
                )
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>{t("Cancel")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default WorkflowEditorTab
